import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  MapPin,
  Mountain,
  Flag,
  Trophy,
  Search,
  Calendar,
  Clock,
  Activity,
  Users,
  Bike,
  Sparkles,
  ArrowDownRight,
  Camera,
  FileText,
  Radio,
  Tv,
  X,
  Menu,
} from "lucide-react";

import "@/App.css";
import {
  kmText,
  dateText,
  weekdayText,
  dateTimeText,
  shortRoute,
  pickInitialStageIndex,
  normalize,
  eventGroupMeta,
  jerseyTheme,
  teamCategoryLabel,
  raceStatus,
  buildStageSchedule,
  STAGE_GUIDE,
  STAGE_TIMES,
} from "@/lib/format";

const API_COMPETITION = "/api/competition_live.php";
const API_RIDER = "/api/rider.php";
const MATS_API_BASE = "https://api.cycling.matsport.com";

const HERO_BG = "/assets/hero/kemer-kalkan.jpg";
const LOGO_TUR = "/assets/logos/tourofturkiye.png";
const LOGO_BP = "/assets/logos/bg.png"; // official BikePedia mark

const JERSEY_IMAGES = {
  turquoise: "/assets/jerseys/turkuaz.png",
  green: "/assets/jerseys/yesil.png",
  red: "/assets/jerseys/kirmizi.png",
  white: "/assets/jerseys/beyaz.png",
};

const COUNTRY_DISPLAY_TR =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames !== "undefined"
    ? new Intl.DisplayNames(["tr"], { type: "region" })
    : null;
const AUTO_REFRESH_MS = 5 * 60 * 1000;
const POST_STAGE_REFRESH_GRACE_MS = 60 * 60 * 1000;
const DEFAULT_VIEW = "etaplar";
const VIEW_META = {
  etaplar: {
    label: "Etaplar",
    href: "#etaplar",
    title: "Etap Merkezi",
  },
  sonuclar: {
    label: "Sonuçlar",
    href: "#sonuclar",
    title: "Sonuç Merkezi",
  },
  takimlar: {
    label: "Start Listesi",
    href: "#takimlar",
    title: "Start Listesi",
  },
};

const RESULTS_MODE_META = {
  stage: { label: "Etap Sonuçları" },
  general: { label: "Genel Klasman" },
};

const RANKING_PANEL_MAP = {
  stage: {
    individual: { label: "Etap Klasmanı", panelKeys: ["individual-stage-classification"] },
    sprints: { label: "Sprint", panelKeys: ["sprints-sprints"] },
    climber: { label: "Tırmanış", panelKeys: ["climber-kom"] },
    treasure: { label: "Türkiye Güzellikleri", panelKeys: ["treasure-treasure-sprints"] },
    teams: { label: "Takımlar", panelKeys: ["teams-stage-classification"] },
  },
  general: {
    individual: { label: "Genel Klasman", panelKeys: ["individual-general-classification"] },
    sprints: { label: "Sprint", panelKeys: ["sprints-sprints-classification"] },
    climber: { label: "Tırmanış", panelKeys: ["climber-best-climber-classification"] },
    treasure: { label: "Türkiye Güzellikleri", panelKeys: ["treasure-treasure-sprints-classification"] },
    teams: { label: "Takımlar", panelKeys: ["teams-general-classification"] },
  },
};

function useRaceStatus(stages) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => raceStatus(stages || []), [stages, tick]);
}

function absoluteMatsportUrl(pathOrUrl) {
  const v = String(pathOrUrl || "").trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("/")) return `${MATS_API_BASE}${v}`;
  return `${MATS_API_BASE}/${v}`;
}

function proxiedDocumentUrl(pathOrUrl) {
  const v = String(pathOrUrl || "").trim();
  if (!v) return null;
  return `/api/document.php?url=${encodeURIComponent(v)}`;
}

function countryNameTr(code) {
  const c = String(code || "").trim().toUpperCase();
  if (!c) return null;
  if (COUNTRY_DISPLAY_TR) {
    try {
      return COUNTRY_DISPLAY_TR.of(c) || c;
    } catch {
      return c;
    }
  }
  return c;
}

function riderStatusTr(status) {
  const s = String(status || "").trim().toUpperCase();
  if (!s) return null;
  switch (s) {
    case "DNS":
      return "Başlamadı";
    case "DNF":
      return "Yarışı Bıraktı";
    case "DSQ":
      return "Diskalifiye";
    case "OTL":
      return "Zaman Limiti Dışı";
    default:
      return status;
  }
}

function riderResultTypeTr(type) {
  const t = String(type || "").trim().toUpperCase();
  switch (t) {
    case "STAGE":
      return "Etap";
    case "OVERALL":
      return "Genel Klasman";
    default:
      return type || "Sonuç";
  }
}

function shouldAutoRefreshNow(stages, nowMs = Date.now()) {
  const schedule = buildStageSchedule(stages || []);
  if (!schedule.length) return false;
  return schedule.some(
    (item) => nowMs >= item.startMs && nowMs <= item.finishMs + POST_STAGE_REFRESH_GRACE_MS
  );
}

function stageHasOfficialResults(stage) {
  if (!stage || typeof stage !== "object") return false;
  const hasEventResults = Array.isArray(stage.events)
    && stage.events.some((ev) => ev?.results?.hasResults && (ev?.results?.rankings || []).length > 0);
  const hasPhotoFinish = Array.isArray(stage?.photoFinish) && stage.photoFinish.length > 0;
  const hasDocuments = Array.isArray(stage?.documents) && stage.documents.length > 0;
  return hasEventResults || hasPhotoFinish || hasDocuments;
}

function stageHasOfficialClassificationData(stage) {
  if (!stage || typeof stage !== "object") return false;
  const hasRankings = Array.isArray(stage?.rankingsCenter?.sections) && stage.rankingsCenter.sections.length > 0;
  const hasJerseyResults = Boolean(stage?.jerseys?.hasResults);
  return hasRankings || hasJerseyResults;
}

function rankingSectionsForMode(sourceSections, mode) {
  const config = RANKING_PANEL_MAP[mode];
  if (!config || !Array.isArray(sourceSections)) return [];

  return Object.entries(config)
    .map(([sectionKey, meta]) => {
      const sourceSection = sourceSections.find((section) => section?.key === sectionKey);
      if (!sourceSection) return null;
      const panels = (sourceSection?.panels || []).filter((panel) => meta.panelKeys.includes(panel?.key));
      if (!panels.length) return null;
      return {
        key: sectionKey,
        label: meta.label,
        panels,
      };
    })
    .filter(Boolean);
}

function viewFromHash(hash) {
  const normalized = String(hash || "")
    .replace(/^#/, "")
    .trim()
    .toLocaleLowerCase("tr-TR");
  if (normalized === "klasmanlar") return "sonuclar";
  return VIEW_META[normalized] ? normalized : DEFAULT_VIEW;
}

function NavBar({ now, onRefresh, refreshing, lastUpdated }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navItems = Object.values(VIEW_META).map((item) => [item.label, item.href]);
  return (
    <header className="tur-nav" data-testid="site-header">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-5">
        {/* Left: TUR logo */}
        <a href="#top" className="flex items-center gap-2.5 sm:gap-3.5 min-w-0" data-testid="brand-link">
          <img
            src={LOGO_TUR}
            alt="61. Tour of Türkiye"
            className="h-10 sm:h-12 w-auto shrink-0"
            style={{ filter: "drop-shadow(0 2px 8px rgba(10,169,193,0.18))" }}
          />
        </a>

        {/* Center: nav links — desktop only */}
        <nav
          className="hidden md:flex items-center gap-1 mx-auto"
          data-testid="primary-nav"
        >
          {navItems.map(([label, href]) => (
            <a
              key={href}
              href={href}
              className="px-4 py-2 rounded-full text-[0.86rem] font-semibold text-[color:var(--ink-2)] hover:text-[color:var(--turquoise-deep)] hover:bg-[color:rgba(10,169,193,0.08)] transition-colors"
              data-testid={`nav-${href.replace("#", "")}`}
            >
              {label}
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 ml-auto md:ml-0">
          <div
            className="hidden lg:flex items-center gap-2 pr-2.5 pl-3 py-1.5 rounded-full"
            style={{
              background: "rgba(10,169,193,0.08)",
              border: "1px solid rgba(10,169,193,0.22)",
            }}
            data-testid="live-status-badge"
          >
            <span className="live-dot" />
            <span className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-[color:var(--turquoise-deep)] font-bold">
              CANLI
            </span>
            <span className="font-mono text-[0.7rem] text-[color:var(--ink-2)]">
              {dateTimeText(lastUpdated || now)}
            </span>
          </div>

          <button
            type="button"
            className="btn-ghost hidden sm:inline-flex"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} />
            <span className="hidden lg:inline">Yenile</span>
          </button>

          <a
            href="https://bikepedia.com.tr"
            target="_blank"
            rel="noreferrer"
            className="ml-1 inline-flex items-center justify-center"
            data-testid="brand-bikepedia"
            title="BikePedia"
          >
            <img src={LOGO_BP} alt="BikePedia" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full" />
          </a>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full"
            style={{
              background: "rgba(10,169,193,0.08)",
              border: "1px solid rgba(10,169,193,0.22)",
              color: "var(--turquoise-deep)",
            }}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menüyü aç/kapat"
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {mobileOpen ? (
        <div
          className="md:hidden border-t"
          style={{
            borderColor: "rgba(10,169,193,0.14)",
            background: "rgba(255,255,255,0.97)",
            backdropFilter: "blur(20px) saturate(140%)",
          }}
          data-testid="mobile-menu-sheet"
        >
          <nav className="max-w-[1280px] mx-auto px-4 py-3 flex flex-col gap-1">
            {navItems.map(([label, href]) => (
              <a
                key={href}
                href={href}
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 rounded-xl text-[0.96rem] font-semibold transition-colors"
                style={{
                  color: "var(--ink)",
                  background: "rgba(10,169,193,0.06)",
                }}
                data-testid={`mobile-nav-${href.replace("#", "")}`}
              >
                {label}
              </a>
            ))}
            <div
              className="mt-2 flex items-center gap-2 px-3 py-2.5 rounded-xl"
              style={{
                background: "rgba(10,169,193,0.08)",
                border: "1px solid rgba(10,169,193,0.22)",
              }}
            >
              <span className="live-dot" />
              <span className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-[color:var(--turquoise-deep)] font-bold">
                CANLI
              </span>
              <span className="font-mono text-[0.72rem] text-[color:var(--ink-2)] ml-auto">
                {dateTimeText(lastUpdated || now)}
              </span>
            </div>
            <button
              type="button"
              className="btn-ghost w-full justify-center mt-1"
              onClick={() => {
                onRefresh && onRefresh();
                setMobileOpen(false);
              }}
              disabled={refreshing}
              data-testid="mobile-refresh-btn"
            >
              <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} />
              Yenile
            </button>
          </nav>
        </div>
      ) : null}
    </header>
  );
}

function HeroSection({ competition, summary, status }) {
  const startDate = competition?.startDate;
  const endDate = competition?.endDate;
  const isOngoing = status?.kind === "ongoing";
  const isCompleted = status?.kind === "completed";
  const parts = status?.parts || { d: 0, h: 0, m: 0, s: 0 };
  return (
    <section
      id="top"
      className="relative w-full max-w-[1280px] mx-auto px-4 sm:px-6 pt-8 pb-12 sm:pt-12 sm:pb-16"
      data-testid="hero-section"
    >
      <div className="hero-card grain">
        <div className="hero-bg">
          <img src={HERO_BG} alt="Tour of Türkiye 2026 — Kemer-Kalkan Etabı" loading="eager" />
        </div>

        <div className="relative z-[1] grid lg:grid-cols-12 gap-6 lg:gap-10 px-5 sm:px-10 pt-10 sm:pt-14 pb-8 sm:pb-12">
          {/* LEFT: Title block */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="kicker justify-center lg:justify-start"
            >
              <span className="bar" />
              T.C. Cumhurbaşkanlığı Himayelerinde · 26 Nisan – 3 Mayıs 2026
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display mt-5 text-balance"
              style={{
                fontSize: "clamp(2.4rem, 5.6vw, 4.8rem)",
                lineHeight: 0.96,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "var(--ink)",
              }}
            >
              <span className="text-grad-turq">61. Cumhurbaşkanlığı</span>
              <br />
              Bisiklet Turu
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.18 }}
              className="mt-5 max-w-xl mx-auto lg:mx-0 text-[1.04rem] sm:text-[1.12rem] leading-relaxed"
              style={{ color: "var(--ink-2)", fontWeight: 500 }}
            >
              BikePedia için Türkiye’nin en prestijli bisiklet yarışı şimdi tek
              merkezde. 8 etap, {summary?.totalKm ? `${Math.round(summary.totalKm)}` : "—"} km boyunca
              Ege kıyılarından Akdeniz’e, oradan başkent Ankara’ya uzanan büyük
              mücadeleyi BikePedia Yarış Merkezi’nden takip edin. Veriler, resmi
              yarış feed’inden anlık olarak güncellenir.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.32 }}
              className="mt-7 flex flex-wrap items-center justify-center lg:justify-start gap-3"
            >
              <a href="#etaplar" className="btn-primary" data-testid="hero-cta-stages">
                Etap Merkezi
                <ArrowDownRight size={15} />
              </a>
              <a href="#sonuclar" className="btn-ghost" data-testid="hero-cta-results">
                <Trophy size={14} /> Sonuçlar & Mayolar
              </a>

              <div
                className="flex items-center justify-center lg:justify-start gap-2 ml-1 text-[0.78rem] font-mono uppercase tracking-[0.2em]"
                style={{ color: "var(--ink-2)" }}
              >
                <Calendar size={13} className="text-[color:var(--turquoise)]" />
                {dateText(startDate)} – {dateText(endDate)}
              </div>
            </motion.div>
          </div>

          {/* RIGHT: dynamic countdown */}
          <motion.aside
            initial={{ opacity: 0, x: 22 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 lg:self-end w-full max-w-[520px] mx-auto lg:mx-0"
          >
            <div
              className="glass-strong p-5 sm:p-6 grain relative overflow-hidden"
              data-testid="countdown-block"
            >
              <div
                className="absolute -right-6 -top-6 w-44 h-44 rounded-full pointer-events-none"
                style={{
                  background: "radial-gradient(closest-side, rgba(10,169,193,0.18), transparent 70%)",
                  filter: "blur(8px)",
                }}
              />
              <div className="relative">
                <div className="flex flex-col sm:flex-row items-center sm:items-start sm:justify-between mb-3 gap-2 sm:gap-3 text-center sm:text-left">
                  <div className="kicker flex items-center justify-center sm:justify-start gap-2">
                    {isOngoing ? <span className="live-dot" /> : <span className="bar" />}
                    {status?.headline || "Yarışa Kalan Süre"}
                  </div>
                  <div
                    className="font-mono text-[0.66rem] tracking-[0.22em] uppercase font-semibold"
                    style={{ color: isOngoing ? "#b91c1c" : "var(--gold)" }}
                  >
                    {isOngoing ? "🔴 CANLI" : isCompleted ? "🏆 TAMAMLANDI" : `Etap ${status?.target?.stage ?? 1}`}
                  </div>
                </div>

                {isCompleted ? (
                  <div className="py-6 text-center">
                    <div className="font-display text-[2.2rem]" style={{ color: "var(--ink)" }}>
                      🏆
                    </div>
                    <div
                      className="font-display text-[1.4rem] mt-2"
                      style={{ color: "var(--ink)", letterSpacing: "-0.01em" }}
                    >
                      Yarış Tamamlandı
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2.5">
                    {[
                      ["GÜN", parts.d],
                      ["SAAT", parts.h],
                      ["DK", parts.m],
                      ["SN", parts.s],
                    ].map(([lbl, val]) => (
                      <div key={lbl} className="metric-card !p-3 text-center">
                        <div
                          className="font-mono"
                          style={{ fontSize: "1.7rem", letterSpacing: "-0.02em", lineHeight: 1, color: "var(--ink)" }}
                        >
                          {String(val).padStart(2, "0")}
                        </div>
                        <div
                          className="text-[0.6rem] mt-1.5 tracking-[0.18em] uppercase font-semibold"
                          style={{ color: "var(--text-muted)" }}
                        >
                          {lbl}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div
                  className="mt-4 pt-4 border-t flex flex-col sm:flex-row items-center sm:justify-between text-[0.74rem] font-mono gap-2 sm:gap-3 text-center sm:text-left"
                  style={{ borderColor: "rgba(10,169,193,0.16)", color: "var(--ink-2)" }}
                >
                  <span>{status?.subline || ""}</span>
                  <span style={{ color: "var(--turquoise-deep)" }}>UCI 2.Pro</span>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>

        {/* Metric strip */}
        <div className="relative z-[1] border-t px-5 sm:px-10 py-5 sm:py-6 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5"
          style={{ borderColor: "rgba(10,169,193,0.14)" }}
        >
          {[
            { label: "Etap", value: summary?.stageCount ?? "—", icon: <Bike size={14} /> },
            { label: "Toplam KM", value: summary?.totalKm ? `${Math.round(summary.totalKm)}` : "—", icon: <Activity size={14} /> },
            { label: "Takım", value: summary?.teamCount ?? "—", icon: <Trophy size={14} /> },
            { label: "Sporcu", value: summary?.riderCount ?? "—", icon: <Users size={14} /> },
          ].map((m) => (
            <div key={m.label} className="metric-card" data-testid={`hero-metric-${m.label.toLowerCase()}`}>
              <div className="flex items-center gap-1.5 lbl">
                {m.icon}
                {m.label}
              </div>
              <div className="num">{m.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ExperienceSwitch({ currentView }) {
  const cards = Object.entries(VIEW_META);
  return (
    <section className="max-w-[1280px] mx-auto px-4 sm:px-6 -mt-2 sm:-mt-4" data-testid="experience-switch">
      <div className="view-switch-grid">
        {cards.map(([key, item]) => {
          const active = currentView === key;
          return (
            <a
              key={key}
              href={item.href}
              className={`view-switch-card ${active ? "active" : ""}`}
              data-testid={`view-switch-${key}`}
            >
              <div className="view-switch-title">{item.title}</div>
            </a>
          );
        })}
      </div>
    </section>
  );
}

function StageSelector({ stages, activeIndex, setActiveIndex }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };
  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="kicker"><span className="bar" /> Etap Seçimi</div>
          <h3
            className="font-display mt-1.5 text-[1.5rem] sm:text-[1.8rem]"
            style={{ letterSpacing: "-0.01em", color: "var(--ink)" }}
          >
            8 Etap · Ege’den Başkent’e
          </h3>
        </div>
        <div className="hidden md:flex items-center gap-1.5">
          <button
            type="button"
            className="btn-ghost !p-2"
            onClick={() => scroll(-1)}
            aria-label="Önceki etap"
            data-testid="stage-scroll-prev"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            className="btn-ghost !p-2"
            onClick={() => scroll(1)}
            aria-label="Sonraki etap"
            data-testid="stage-scroll-next"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div
        ref={ref}
        className="grid grid-flow-col auto-cols-[minmax(240px,1fr)] sm:auto-cols-[minmax(0,1fr)] sm:grid-flow-row sm:grid-cols-4 gap-3 overflow-x-auto sm:overflow-visible pb-2 scroll-snap-x"
        data-testid="stage-selector-carousel"
      >
        {stages.map((stage, idx) => {
          const stats = stage?.stats || {};
          const isActive = idx === activeIndex;
          const guide = STAGE_GUIDE[Number(stage?.stage)] || null;
          return (
            <motion.button
              key={stage.id}
              type="button"
              onClick={() => setActiveIndex(idx)}
              className={`stage-card ${isActive ? "active" : ""}`}
              whileHover={{ y: -2 }}
              data-testid={`stage-card-${stage.stage}`}
            >
              <span className="stage-num">Etap {stage.stage} · {dateText(stage.date)}</span>
              <span className="stage-route">{shortRoute(stage.name)}</span>
              <div className="stage-pills">
                <span className="chip sprint">S {stats.sprint || 0}</span>
                <span className="chip mountain">T {stats.mountain || 0}</span>
                <span className="chip treasure">TG {stats.treasure || 0}</span>
              </div>
              {guide ? (
                <div className="stage-guide-line" data-testid={`stage-guide-${stage.stage}`}>
                  <span className="stage-guide-item">
                    <Tv size={12} />
                    <span>{guide.broadcast}</span>
                    <span className="stage-guide-separator">-</span>
                    <span>{String(guide.broadcasters || "—").replace(/\s*·\s*/g, " - ")}</span>
                  </span>
                </div>
              ) : null}
              <div className="stage-meta">
                <span className="stage-km">
                  {stage.length ? Math.round(stage.length) : "—"}
                  <span className="unit">km</span>
                </span>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

function ImageLightbox({ src, alt, open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 sm:p-8"
      style={{ background: "rgba(3,4,8,0.92)", backdropFilter: "blur(18px)" }}
      onClick={onClose}
      data-testid="stage-image-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[0.84rem] font-semibold"
        style={{
          background: "rgba(255,255,255,0.95)",
          color: "var(--ink)",
          border: "1px solid rgba(255,255,255,0.6)",
          boxShadow: "0 8px 22px rgba(0,0,0,0.4)",
        }}
        data-testid="lightbox-close"
      >
        <X size={16} /> Kapat
      </button>
      <img
        src={src}
        alt={alt}
        className="max-w-full max-h-[92vh] rounded-xl shadow-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

function DocumentModal({ src, title, open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="document-modal-backdrop"
      onClick={onClose}
      data-testid="document-modal"
    >
      <div className="document-modal-card" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="document-modal-close"
          data-testid="document-modal-close"
        >
          <X size={16} /> Kapat
        </button>
        <div className="document-modal-head">
          <div className="document-modal-kicker">Resmi Hakem Sonucu</div>
          <div className="document-modal-title">{title || "Communique"}</div>
        </div>
        <div className="document-modal-actions">
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            className="race-center-btn ghost"
          >
            <FileText size={14} /> PDF'i yeni sekmede aç
          </a>
        </div>
        <object
          data={src}
          type="application/pdf"
          aria-label={title || "Resmi Hakem Sonucu"}
          className="document-modal-frame"
        >
          <div className="document-modal-fallback">
            <div className="document-modal-fallback-title">PDF önizlemesi açılamadı.</div>
            <a href={src} target="_blank" rel="noreferrer" className="race-center-btn">
              <FileText size={14} /> Resmi Hakem Sonucunu Aç
            </a>
          </div>
        </object>
      </div>
    </div>
  );
}

function StageDetail({ stage, activeIndex }) {
  if (!stage) return null;
  const events = stage.events || [];
  const length = Number(stage.length) || 0;
  const stats = stage.stats || {};
  const stageNum = stage.stage || activeIndex + 1;
  const officialMap = `/assets/stages/rota${stageNum}.jpg`;
  const officialProfile = stageNum <= 7 ? `/assets/profiles/profile${stageNum}.jpg` : null;
  const guide = STAGE_GUIDE[stageNum] || null;
  const times = STAGE_TIMES[stageNum] || null;
  const broadcastText = guide?.broadcast
    ? `${guide.broadcast} - ${String(guide.broadcasters || "—").replace(/\s*·\s*/g, " - ")}`
    : null;

  // Build marker label per event matching elevation icon language
  const markerText = (ev) => {
    if (ev.group === "mountain") {
      const cat = String(ev.category || "").trim();
      return cat ? cat : "M";
    }
    if (ev.group === "sprint") return "S";
    if (ev.group === "treasure") return "TG";
    if (ev.group === "finish") return "F";
    return "·";
  };

  const pointSummaryLines = (ev) => {
    const group = String(ev.group || "").toLowerCase();
    if (group === "sprint" || group === "treasure") {
      return [
        { label: "Sprint Puanı", value: "1. 5p · 2. 3p · 3. 1p" },
        { label: "Zaman Bonusu", value: "1. 3sn · 2. 2sn · 3. 1sn" },
      ];
    }
    if (group === "mountain") {
      const cat = String(ev.category || "").trim().toUpperCase();
      const byCat = {
        HC: [15, 12, 10, 8, 5],
        "1": [10, 7, 5, 3, 1],
        "2": [5, 3, 2, 1],
        "3": [3, 2, 1],
      };
      const scores = byCat[cat] || byCat["3"];
      return [
        {
          label: `Tırmanış Puanı${cat ? ` · Kategori ${cat}` : ""}`,
          value: scores.map((score, idx) => `${idx + 1}. ${score}p`).join(" · "),
        },
      ];
    }
    if (group === "finish") {
      return [
        {
          label: "Etap Puanı",
          value: "1-5: 15/14/13/12/11p · 6-10: 10/9/8/7/6p · 11-15: 5/4/3/2/1p",
        },
        { label: "Finiş Bonusu", value: "1. 10sn · 2. 6sn · 3. 4sn" },
      ];
    }
    return [];
  };

  return (
    <article className="glass grain p-5 sm:p-7 mt-6" data-testid="stage-detail-card">
      {/* Title row */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <div
              className="font-mono text-[0.78rem] tracking-[0.22em] uppercase font-bold"
              style={{ color: "var(--turquoise-deep)" }}
            >
              ETAP {stage.stage}
            </div>
            <span className="chip">
              <Calendar size={11} /> {dateText(stage.date)} · {weekdayText(stage.date)}
            </span>
            <span className="chip">
              <MapPin size={11} /> {kmText(length)}
            </span>
            {times ? (
              <>
                <span className="chip" data-testid="stage-time-start">
                  <Clock size={11} /> Başlangıç {times.start}
                </span>
                <span className="chip">
                  Tahmini Finiş {times.finish}
                </span>
                <span className="chip" title="Nötr başlangıç mesafesi">
                  Nötr +{times.neutral} km
                </span>
              </>
            ) : null}
            {broadcastText ? (
              <span className="chip" data-testid="stage-broadcast-window">
                <Radio size={11} /> Canlı Yayın {broadcastText}
              </span>
            ) : null}
          </div>
          <h2
            className="font-display mt-3"
            style={{ fontSize: "clamp(1.8rem, 3.6vw, 2.8rem)", letterSpacing: "-0.015em", lineHeight: 1.05, color: "var(--ink)" }}
            data-testid="stage-title"
          >
            {shortRoute(stage.name)}
          </h2>
          {times || broadcastText ? (
            <div className="mt-2.5 font-mono text-[0.84rem] font-semibold" style={{ color: "var(--ink-2)" }}>
              {times ? (
                <>
                  İmza Töreni <span style={{ color: "var(--ink)" }}>{times.signing}</span> · Başlangıç <span style={{ color: "var(--ink)" }}>{times.start}</span> · Tahmini Varış <span style={{ color: "var(--ink)" }}>{times.finish}</span>
                </>
              ) : null}
              {broadcastText ? (
                <>
                  {times ? " · " : null}
                  Canlı Yayın <span style={{ color: "var(--ink)" }}>{broadcastText}</span>
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-2.5 w-full sm:w-auto sm:min-w-[300px]" data-testid="stage-stats">
          <div className="metric-card !p-3.5">
            <div className="lbl stage-stat-label !mb-2 flex items-center justify-center gap-1.5">
              <Flag size={13} className="shrink-0 text-emerald-700" />
              <span>Sprint</span>
            </div>
            <div className="num stage-stat-value !text-[1.6rem]">{stats.sprint || 0}</div>
          </div>
          <div className="metric-card !p-3.5">
            <div className="lbl stage-stat-label !mb-2 flex items-center justify-center gap-1.5">
              <Mountain size={13} className="shrink-0 text-red-600" />
              <span>Tırmanış</span>
            </div>
            <div className="num stage-stat-value !text-[1.6rem]">{stats.mountain || 0}</div>
          </div>
          <div className="metric-card !p-3.5">
            <div className="lbl stage-stat-label !mb-2 flex items-center justify-center gap-1.5">
              <Sparkles size={13} className="shrink-0 text-blue-700" />
              <span className="hidden sm:inline">Türkiye Güzellikleri</span>
              <span className="sm:hidden">Türkiye Güz.</span>
            </div>
            <div className="num stage-stat-value !text-[1.6rem]">{stats.treasure || 0}</div>
          </div>
        </div>
      </div>

      {/* Official stage map */}
      <div className="mt-7">
        <div
          className="relative rounded-2xl overflow-hidden border"
          style={{
            borderColor: "rgba(10,169,193,0.2)",
            background: "#fff",
          }}
          data-testid="stage-official-map"
        >
          <img
            src={officialMap}
            alt={`${stage.name} etap haritası`}
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
      </div>

      {/* Combined elevation + km profile */}
      <div className="mt-6">
        <div className="kicker mb-2.5"><span className="bar" /> Yükseklik Profili · KM Profili</div>
        <div
          className="profile-shell"
          data-testid="stage-profile-combined"
        >
          {officialProfile ? (
            <img
              src={officialProfile}
              alt={`${stage.name} yükseklik profili`}
              className="profile-img"
              loading="lazy"
            />
          ) : (
            <div
              className="px-6 py-12 text-[0.92rem] text-center"
              style={{ color: "var(--ink-2)", background: "linear-gradient(180deg,#ffffff,#f4fafc)" }}
            >
              Bu etap (Ankara → Ankara) için resmi yükseklik profili henüz yayınlanmadı. KM bazlı nokta dağılımı aşağıda.
            </div>
          )}

          {/* KM rail aligned under the elevation chart */}
          <div id="stage-km-rail" className="profile-rail" data-testid="stage-km-rail">
            <span className="rail-km start">0 KM</span>
            <span className="rail-km end">{kmText(length)}</span>
            <div className="rail-track" />
            <div className="marker-host">
              {events.map((ev, i) => {
                if (length <= 0 || ev.km == null) return null;
                const pos = Math.min(99.5, Math.max(0.5, (Number(ev.km) / length) * 100));
                const meta = eventGroupMeta(ev.group);
                const kmNum = Number(ev.km);
                const kmLabel = Number.isInteger(kmNum) ? `${kmNum}` : kmNum.toFixed(1).replace(".", ",");
                return (
                  <React.Fragment key={`${ev.id || i}-${ev.km}`}>
                    <div
                      className={`km-marker ${ev.group}`}
                      style={{ left: `${pos}%` }}
                      title={`${meta.label} · ${kmText(ev.km, 1)}`}
                    >
                      {markerText(ev)}
                    </div>
                    <div className="km-label" style={{ left: `${pos}%` }}>
                      {kmLabel} km
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-[0.74rem] font-mono"
          style={{ color: "var(--ink-2)" }}
        >
          <span className="flex items-center gap-1.5"><span className="km-marker legend-dot sprint">S</span>Sprint Kapısı</span>
          <span className="flex items-center gap-1.5"><span className="km-marker legend-dot mountain">3</span>Tırmanış Kapısı (HC · 1 · 2 · 3)</span>
          <span className="flex items-center gap-1.5"><span className="km-marker legend-dot treasure">TG</span>Türkiye Güzellikleri Sprint Kapısı</span>
          <span className="flex items-center gap-1.5"><span className="km-marker legend-dot finish">F</span>Finiş</span>
        </div>
      </div>

      {/* Events grid */}
      <div className="mt-7" data-testid="stage-events-list">
        <div className="kicker mb-3"><span className="bar" /> Etap Noktaları</div>
        {events.length === 0 ? (
          <p className="text-[0.92rem]" style={{ color: "var(--ink-2)" }}>
            Bu etap için nokta verisi henüz yok.
          </p>
        ) : (
          <div className="event-plain-list" data-testid="stage-event-points-list">
            {events.map((ev, i) => {
              const meta = eventGroupMeta(ev.group);
              const pointLines = pointSummaryLines(ev);
              return (
                <div
                  key={`${ev.id || i}-${ev.km || i}`}
                  className={`event-plain-item event-plain-item-${meta.chip || "neutral"}`}
                  data-testid="stage-event-row"
                >
                  <div className="event-plain-main">
                    <div className="event-plain-heading">
                      <span className={`event-symbol event-symbol-${meta.chip || "neutral"}`}>
                        {markerText(ev)}
                      </span>
                      <div className="event-plain-title-wrap">
                        <span className="event-plain-title">{meta.label}</span>
                        {ev.category ? (
                          <span className="event-plain-subtitle"> · Kategori {ev.category}</span>
                        ) : null}
                      </div>
                    </div>
                    {pointLines.length ? (
                      <div className="event-plain-points">
                        {pointLines.map((line, lineIdx) => (
                          <div key={`${line.label}-${lineIdx}`} className="event-plain-line">
                            <span className="event-plain-label">{line.label}:</span>
                            <span className="event-plain-value">{line.value}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </article>
  );
}

function StageResultsSection({ stages, activeIndex }) {
  const stage = stages[activeIndex] || null;
  const riderRequestRef = useRef(0);
  const [selectedRider, setSelectedRider] = useState(null);
  const [riderDetails, setRiderDetails] = useState(null);
  const [riderLoading, setRiderLoading] = useState(false);
  const [riderError, setRiderError] = useState(null);
  const [liveTab, setLiveTab] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [documentModal, setDocumentModal] = useState(null);

  const liveStage = stage || null;
  const liveEvents = useMemo(() => {
    return (liveStage?.events || []).filter(
      (ev) => ev?.results?.hasResults && (ev?.results?.rankings || []).length > 0
    );
  }, [liveStage]);

  const liveTabs = useMemo(() => {
    const base = [
      { key: "all", label: "Tümü" },
      { key: "mountain", label: "Tırmanış" },
      { key: "treasure", label: "Türkiye Güzellikleri" },
      { key: "sprint", label: "Sprint" },
      { key: "finish", label: "Finiş" },
    ];
    return base.filter((tab, idx) => idx === 0 || liveEvents.some((ev) => ev.group === tab.key));
  }, [liveEvents]);

  useEffect(() => {
    if (liveTab === "all") return;
    if (!liveEvents.some((ev) => ev.group === liveTab)) {
      setLiveTab("all");
    }
  }, [liveEvents, liveTab]);

  const filteredLiveEvents =
    liveTab === "all" ? liveEvents : liveEvents.filter((ev) => ev.group === liveTab);

  const selectedStageTitle = stage
    ? `Etap ${stage.stage || "-"} Sonuç Merkezi`
    : "Etap Sonuç Merkezi";
  const selectedStageRoute = stage ? shortRoute(stage.name || "") : null;
  const selectedStageDate = stage?.date ? dateText(stage.date) : null;
  const selectedStageSummary = [selectedStageRoute, selectedStageDate].filter(Boolean).join(" · ");
  const stageFeedNote = stageHasOfficialResults(stage)
    ? ""
    : "Seçili etap için resmi etap sonucu henüz yayınlanmadı. Yarış başladığında kapı geçişleri ve finiş akışı burada görünecek.";

  const raceStarted = Boolean(liveEvents.length);
  const photoFinishItems = Array.isArray(stage?.photoFinish) ? stage.photoFinish : [];
  const documents = Array.isArray(stage?.documents) ? stage.documents : [];
  const featuredPhoto = photoFinishItems[0] || null;
  const featuredPhotoUrl = absoluteMatsportUrl(featuredPhoto?.image || null);
  const featuredCommunique =
    documents.find((doc) => String(doc?.kind || "").toLowerCase() === "communique") ||
    documents[0] ||
    null;
  const featuredDocumentUrl = proxiedDocumentUrl(featuredCommunique?.url || null);

  const riderResultValue = (entry) => {
    const raw = entry?.value || entry?.capital || entry?.bonus || entry?.gap || null;
    if (!raw) return null;
    return String(raw)
      .replace(/\bpts?\b/gi, "p")
      .replace(/\bsec\b/gi, "sn")
      .replace(/\s+/g, " ")
      .trim();
  };

  const closeRiderModal = () => {
    riderRequestRef.current += 1;
    setSelectedRider(null);
    setRiderDetails(null);
    setRiderLoading(false);
    setRiderError(null);
  };

  const openRiderModal = async (entry, context) => {
    if (!entry || typeof entry !== "object") return;
    const raceContextInput =
      context && typeof context === "object" ? context : { title: context || "Etap Sonucu" };

    const nat = String(entry?.nationality || "")
      .trim()
      .toUpperCase();
    const image =
      absoluteMatsportUrl(entry?.image) ||
      absoluteMatsportUrl(entry?.photo) ||
      (entry?.uciid ? absoluteMatsportUrl(`/images/riders/${entry.uciid}/${entry.uciid}.png`) : null);

    const base = {
      uciid: entry?.uciid || null,
      name: entry?.name || (entry?.bib ? `Sporcu #${entry.bib}` : "Sporcu"),
      teamName: entry?.team || null,
      teamCode: entry?.teamCode || null,
      teamCategory: entry?.teamCategory || null,
      bib: entry?.bib || null,
      nationality: nat || null,
      countryNameTr: countryNameTr(nat) || null,
      image,
      birthDate: entry?.birthDate || null,
      status: entry?.status || null,
      contextTitle: raceContextInput?.title || "Etap Sonucu",
      rank: entry?.position || raceContextInput?.rank || null,
      resultValue: riderResultValue(entry) || raceContextInput?.resultValue || null,
      raceContext: {
        title: raceContextInput?.title || "Etap Sonucu",
        stageNumber: raceContextInput?.stageNumber || null,
        stageName: raceContextInput?.stageName || null,
        stageDate: raceContextInput?.stageDate || null,
        gateLabel: raceContextInput?.gateLabel || null,
        gateType: raceContextInput?.gateType || null,
        gateCategory: raceContextInput?.gateCategory || null,
        gateKm:
          raceContextInput?.gateKm === null || raceContextInput?.gateKm === undefined
            ? null
            : Number(raceContextInput.gateKm),
        sourceLabel: raceContextInput?.sourceLabel || null,
      },
      placeOfBirth: null,
      age: null,
      height: null,
      weight: null,
      socials: [],
      topResults: [],
    };

    setSelectedRider(entry);
    setRiderDetails(base);
    setRiderError(null);

    if (!entry?.uciid) return;

    const reqId = ++riderRequestRef.current;
    setRiderLoading(true);
    try {
      const res = await axios.get(API_RIDER, { params: { uciid: entry.uciid } });
      if (riderRequestRef.current !== reqId) return;

      const detail = res?.data?.data;
      if (!detail || typeof detail !== "object") {
        setRiderLoading(false);
        return;
      }

      const detailNat = String(detail.nationality || base.nationality || "")
        .trim()
        .toUpperCase();

      setRiderDetails((prev) => ({
        ...(prev || base),
        ...detail,
        name: detail.name || prev?.name || base.name,
        teamName: detail.teamName || prev?.teamName || base.teamName,
        teamCode: detail.teamCode || prev?.teamCode || base.teamCode,
        teamCategory: detail.teamCategory || prev?.teamCategory || base.teamCategory,
        nationality: detailNat || prev?.nationality || base.nationality,
        countryNameTr:
          countryNameTr(detailNat) ||
          detail.countryName ||
          prev?.countryNameTr ||
          base.countryNameTr,
        image:
          absoluteMatsportUrl(detail.image) ||
          absoluteMatsportUrl(detail.photo) ||
          prev?.image ||
          base.image,
        contextTitle: prev?.contextTitle || base.contextTitle,
        rank: prev?.rank || base.rank,
        resultValue: prev?.resultValue || base.resultValue,
        raceContext: prev?.raceContext || base.raceContext,
      }));
    } catch (err) {
      if (riderRequestRef.current !== reqId) return;
      setRiderError(err?.response?.data?.error || err.message || "Sporcu detayı alınamadı.");
    } finally {
      if (riderRequestRef.current === reqId) {
        setRiderLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedRider) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeRiderModal();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedRider]);

  return (
    <section className="max-w-[1280px] mx-auto mt-6" data-testid="stage-results-section">
      <div className="official-panel-card stage-results-shell">
        <div className="results-stage-section-head">
          <div>
            <div className="kicker"><span className="bar" /> Etap Sonuçları</div>
            <div className="results-stage-section-title">{selectedStageTitle}</div>
            <div className="results-stage-section-copy">
              {selectedStageSummary || "Seçili etap için resmi geçiş ve finiş akışı"}
            </div>
          </div>
          <span
            className="results-stage-status"
            style={{
              background: raceStarted ? "rgba(16,185,129,0.12)" : "rgba(10,169,193,0.1)",
              color: raceStarted ? "#047857" : "var(--turquoise-deep)",
              border: `1px solid ${raceStarted ? "rgba(16,185,129,0.3)" : "rgba(10,169,193,0.25)"}`,
            }}
          >
            {raceStarted ? "Resmi Sonuç Akışı" : "Resmi Sonuç Bekleniyor"}
          </span>
        </div>

        <div className="results-stage-note stage-results-note" style={{ textAlign: "left", maxWidth: "100%" }}>
          {stageFeedNote}
        </div>

        {liveEvents.length === 0 ? (
          <div className="mt-4 official-table-card results-stage-empty">
            <div className="font-display text-[0.98rem]" style={{ color: "var(--ink)" }}>
              Etap sonucu bekleniyor
            </div>
          </div>
        ) : (
          <>
            <div className="live-tabs mt-4" data-testid="stage-live-tabs">
              {liveTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setLiveTab(tab.key)}
                  className={`live-tab-btn ${liveTab === tab.key ? "active" : ""}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="live-event-grid mt-4" data-testid="stage-live-grid">
              {filteredLiveEvents.map((ev, evIdx) => {
                const meta = eventGroupMeta(ev.group);
                const rankings = (ev?.results?.rankings || []).slice(0, 3);
                const contextTitle = `${shortRoute(liveStage?.name || "")} · ${meta.label}`;

                return (
                  <article
                    key={`${ev.id || evIdx}-${ev.km || evIdx}`}
                    className={`live-event-card live-event-card-${meta.chip || "neutral"}`}
                  >
                    <div className="live-event-head">
                      <div className="live-event-head-left">
                        <span className={`chip ${meta.chip}`}>{meta.short}</span>
                        <span className={`event-symbol event-symbol-${meta.chip || "neutral"}`}>
                          {meta.iconText || "·"}
                        </span>
                      </div>
                      <span className="event-km-badge">{kmText(ev.km, 1)}</span>
                    </div>
                    <div className="live-event-title">{meta.label}</div>
                    <div className="live-event-sub">{ev?.results?.label || "Geçiş Sonucu"}</div>

                    <div className="live-event-list">
                      {rankings.map((row, rowIdx) => {
                        const natCode = String(row?.nationality || "").trim().toUpperCase();
                        const flagUrl = natCode ? `${MATS_API_BASE}/images/flags/${natCode}.png` : null;
                        const riderMeta = row?.team || row?.teamName || row?.teamCode || null;
                        return (
                          <button
                            key={`${ev.id || evIdx}-rank-${row?.position || rowIdx}`}
                            type="button"
                            className="live-event-row"
                            onClick={() =>
                              openRiderModal(row, {
                                title: contextTitle,
                                stageNumber: liveStage?.stage || null,
                                stageName: liveStage?.name || null,
                                stageDate: liveStage?.date || null,
                                gateLabel: meta.label || null,
                                gateType: meta.short || null,
                                gateCategory: ev?.category || null,
                                gateKm: ev?.km ?? null,
                                sourceLabel: ev?.results?.label || null,
                                rank: row?.position || rowIdx + 1,
                                resultValue: riderResultValue(row),
                              })
                            }
                          >
                            <span className="live-event-rank">{row?.position || rowIdx + 1}</span>
                            <span className="live-event-rider">
                              <span className="live-event-rider-main">
                                {flagUrl ? (
                                  <img
                                    src={flagUrl}
                                    alt={natCode}
                                    className="rider-flag"
                                    loading="lazy"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ) : null}
                                <span className="live-event-rider-name">
                                  {row?.name || (row?.bib ? `Sporcu #${row.bib}` : "Sporcu")}
                                </span>
                              </span>
                              <span className="live-event-rider-meta">{riderMeta || "Takım bilgisi bekleniyor"}</span>
                            </span>
                            <span className="live-event-points">{riderResultValue(row) || "—"}</span>
                          </button>
                        );
                      })}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}

        <div className="stage-results-utility-grid mt-4">
          <article className="race-center-card media">
            <div className="race-center-card-head">
              <span className="race-center-icon"><Camera size={16} /></span>
              <div>
                <div className="race-center-kicker">Resmi Kayıt</div>
                <div className="race-center-title">Photo Finish</div>
              </div>
            </div>
            {featuredPhotoUrl ? (
              <button
                type="button"
                className="race-center-photo"
                onClick={() =>
                  setLightbox({
                    src: featuredPhotoUrl,
                    alt: featuredPhoto?.label || `${stage?.name} photo finish`,
                  })
                }
              >
                <img src={featuredPhotoUrl} alt={featuredPhoto?.label || "Photo finish"} loading="lazy" />
              </button>
            ) : (
              <div className="race-center-placeholder">Photo finish görseli henüz yüklenmedi.</div>
            )}
          </article>

          <article className="race-center-card">
            <div className="race-center-card-head">
              <span className="race-center-icon"><FileText size={16} /></span>
              <div>
                <div className="race-center-kicker">Resmi Belge</div>
                <div className="race-center-title">Hakem Sonucu</div>
              </div>
            </div>
            <div className="race-center-actions">
              {featuredDocumentUrl ? (
                <button
                  type="button"
                  className="race-center-btn"
                  onClick={() =>
                    setDocumentModal({
                      src: featuredDocumentUrl,
                      title: featuredCommunique?.label || "Resmi Hakem Sonucu",
                    })
                  }
                >
                  <FileText size={14} /> Resmi Hakem Sonucu
                </button>
              ) : null}
              {featuredPhotoUrl ? (
                <button
                  type="button"
                  className="race-center-btn ghost"
                  onClick={() =>
                    setLightbox({
                      src: featuredPhotoUrl,
                      alt: featuredPhoto?.label || `${stage?.name} photo finish`,
                    })
                  }
                >
                  <Camera size={14} /> Photo Finish Aç
                </button>
              ) : null}
            </div>
            {!featuredDocumentUrl ? (
              <div className="race-center-note">Hakem sonucu PDF'i henüz yayınlanmadı.</div>
            ) : null}
          </article>
        </div>
      </div>

      <ImageLightbox
        open={!!lightbox}
        src={lightbox?.src}
        alt={lightbox?.alt}
        onClose={() => setLightbox(null)}
      />
      <DocumentModal
        open={!!documentModal}
        src={documentModal?.src}
        title={documentModal?.title}
        onClose={() => setDocumentModal(null)}
      />

      {selectedRider ? (
        <div
          className="fixed inset-0 z-50 p-4 sm:p-6 grid place-items-center"
          style={{ background: "rgba(3,4,8,0.72)", backdropFilter: "blur(10px)" }}
          onClick={closeRiderModal}
          data-testid="stage-rider-modal"
        >
          <div className="rider-modal-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={closeRiderModal}
              className="rider-modal-close"
              aria-label="Kapat"
            >
              <X size={16} />
            </button>

            <div className="rider-modal-head">
              {riderDetails?.image ? (
                <img
                  src={riderDetails.image}
                  alt={riderDetails.name || "Sporcu"}
                  className="rider-modal-avatar"
                />
              ) : (
                <span className="rider-modal-avatar rider-modal-avatar-fallback">
                  {(riderDetails?.name || "S").charAt(0).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <div className="font-display text-[1.25rem] sm:text-[1.5rem] leading-tight truncate" style={{ color: "var(--ink)" }}>
                  {riderDetails?.name || "Sporcu"}
                </div>
                <div className="mt-1 text-[0.82rem] font-mono tracking-[0.08em] uppercase truncate" style={{ color: "var(--ink-2)" }}>
                  {riderDetails?.teamName || riderDetails?.teamCode || "Takım bilgisi yok"}
                </div>
                <div className="mt-2 flex items-center flex-wrap gap-2">
                  <span className="chip">#{riderDetails?.bib || "—"}</span>
                  {riderDetails?.nationality ? (
                    <span className="chip">
                      <img
                        src={`${MATS_API_BASE}/images/flags/${String(riderDetails.nationality).toUpperCase()}.png`}
                        alt={riderDetails.nationality}
                        className="rider-flag"
                      />
                      {riderDetails.nationality}
                    </span>
                  ) : null}
                  <span className="chip">{riderDetails?.countryNameTr || "Ülke bilgisi yok"}</span>
                </div>
              </div>
            </div>

            <div className="rider-modal-grid">
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Etap</div>
                <div className="mt-1.5 font-semibold leading-tight" style={{ color: "var(--ink)" }}>
                  {riderDetails?.raceContext?.stageNumber
                    ? `Etap ${riderDetails.raceContext.stageNumber} · ${shortRoute(
                        riderDetails?.raceContext?.stageName || ""
                      )}`
                    : "Etap bilgisi bekleniyor"}
                </div>
                <div className="text-[0.78rem] mt-1 font-mono" style={{ color: "var(--ink-2)" }}>
                  {riderDetails?.raceContext?.stageDate
                    ? dateText(riderDetails.raceContext.stageDate)
                    : "Tarih bekleniyor"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Kapı / Sonuç</div>
                <div className="mt-1.5 font-semibold leading-tight" style={{ color: "var(--ink)" }}>
                  {riderDetails?.raceContext?.gateLabel || riderDetails?.contextTitle || "Etap Sonucu"}
                </div>
                <div className="text-[0.78rem] mt-1 font-mono" style={{ color: "var(--ink-2)" }}>
                  {[
                    riderDetails?.raceContext?.gateType || null,
                    riderDetails?.raceContext?.gateKm !== null &&
                    riderDetails?.raceContext?.gateKm !== undefined
                      ? kmText(riderDetails.raceContext.gateKm, 1)
                      : null,
                    riderDetails?.raceContext?.gateCategory
                      ? `Kategori ${riderDetails.raceContext.gateCategory}`
                      : null,
                    riderDetails?.raceContext?.sourceLabel || null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "Detay bekleniyor"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Derece</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.rank ? `${riderDetails.rank}. sıra` : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Sonuç</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.resultValue || "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Doğum Tarihi</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.birthDate ? dateText(riderDetails.birthDate) : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Yaş</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.age ?? "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Boy</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.height ? `${riderDetails.height} cm` : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Kilo</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.weight ? `${riderDetails.weight} kg` : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Doğum Yeri</div>
                <div className="mt-1.5 font-semibold truncate" style={{ color: "var(--ink)" }}>
                  {riderDetails?.placeOfBirth || "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Durum</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderStatusTr(riderDetails?.status) || "Aktif"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Takım Kategori</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {teamCategoryLabel(riderDetails?.teamCategory || "") || "—"}
                </div>
              </div>
            </div>

            {(riderDetails?.topResults || []).length > 0 ? (
              <div className="mt-4">
                <div className="kicker mb-2"><span className="bar" /> Öne Çıkan Sonuçlar</div>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {riderDetails.topResults.slice(0, 4).map((r, i) => (
                    <div key={`${r.raceName || "race"}-${i}`} className="event-tile">
                      <div className="font-semibold text-[0.93rem]" style={{ color: "var(--ink)" }}>
                        {r.raceName || "Yarış"}
                      </div>
                      <div className="text-[0.8rem] mt-1 font-mono" style={{ color: "var(--ink-2)" }}>
                        {r.date || "—"} · {riderResultTypeTr(r.type)} · {r.pos ? `${r.pos}.` : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {riderLoading ? (
              <div className="mt-4 text-[0.85rem] font-semibold" style={{ color: "var(--turquoise-deep)" }}>
                Sporcu detayı yükleniyor…
              </div>
            ) : null}
            {riderError ? (
              <div className="mt-4 text-[0.85rem] font-semibold" style={{ color: "#b91c1c" }}>
                {riderError}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ClassificationSection({ stages, activeIndex, jerseyLeaders, setActiveIndex }) {
  const stage = stages[activeIndex] || null;
  const riderRequestRef = useRef(0);
  const [selectedRider, setSelectedRider] = useState(null);
  const [riderDetails, setRiderDetails] = useState(null);
  const [riderLoading, setRiderLoading] = useState(false);
  const [riderError, setRiderError] = useState(null);
  const [resultsMode, setResultsMode] = useState("stage");
  const [rankingSection, setRankingSection] = useState("");

  const latestClassificationStage = useMemo(() => {
    let latest = null;
    for (const s of stages || []) {
      if (stageHasOfficialClassificationData(s)) {
        latest = s;
      }
    }
    return latest;
  }, [stages]);

  const stageResultsStage = stage || null;
  const generalResultsStage =
    stage && stageHasOfficialClassificationData(stage)
      ? stage
      : latestClassificationStage || stage || null;

  const stageRankingSections = useMemo(
    () => rankingSectionsForMode(stageResultsStage?.rankingsCenter?.sections || [], "stage"),
    [stageResultsStage?.rankingsCenter?.sections]
  );
  const generalRankingSections = useMemo(
    () => rankingSectionsForMode(generalResultsStage?.rankingsCenter?.sections || [], "general"),
    [generalResultsStage?.rankingsCenter?.sections]
  );

  const visibleRankingSections = resultsMode === "stage" ? stageRankingSections : generalRankingSections;
  const activeRankingSection =
    visibleRankingSections.find((section) => section?.key === rankingSection) ||
    visibleRankingSections[0] ||
    null;

  const stageJerseys = generalResultsStage?.jerseys || null;
  const latestSource = jerseyLeaders?.sourceStage || null;
  const leaders = useMemo(() => {
    const fromStage = Array.isArray(stageJerseys?.leaders) ? stageJerseys.leaders : [];
    if (fromStage.length) return fromStage;
    return Array.isArray(jerseyLeaders?.leaders) ? jerseyLeaders.leaders : [];
  }, [stageJerseys?.leaders, jerseyLeaders?.leaders]);

  const selectedStageRoute = stage ? shortRoute(stage.name || "") : null;
  const selectedStageDate = stage?.date ? dateText(stage.date) : null;
  const selectedStageSummary = [selectedStageRoute, selectedStageDate].filter(Boolean);
  const activeModeMeta = RESULTS_MODE_META[resultsMode];

  const stageFeedNote = (() => {
    if (resultsMode === "stage") {
      return stageRankingSections.length
        ? ""
        : "Seçili etap için resmi etap klasmanı henüz yayınlanmadı.";
    }
    if (!generalResultsStage) {
      return "Genel klasman verisi henüz yayınlanmadı.";
    }
    if (stage && generalResultsStage && stage.id && generalResultsStage.id && stage.id !== generalResultsStage.id) {
      return `Seçili etap için genel klasman henüz güncellenmedi. Son resmi durum Etap ${generalResultsStage.stage || "-"} sonu itibarıyla gösteriliyor.`;
    }
    return `Genel klasmanlar Etap ${generalResultsStage.stage || "-"} sonundaki resmi tabloyu gösterir.`;
  })();

  const jerseyNote = (() => {
    if (!leaders.length) return "Lider mayo verisi henüz yayınlanmadı.";
    if (stageJerseys?.hasResults && generalResultsStage) {
      return `Lider mayolar Etap ${generalResultsStage.stage || "-"} sonundaki resmi durumu yansıtır.`;
    }
    if (latestSource?.hasResults) {
      return `Son resmi liderlik Etap ${latestSource.stage || "-"} üzerinden gösteriliyor.`;
    }
    return "Lider mayo verisi bekleniyor.";
  })();

  useEffect(() => {
    if (!visibleRankingSections.length) {
      if (rankingSection !== "") setRankingSection("");
      return;
    }
    if (!visibleRankingSections.some((section) => section?.key === rankingSection)) {
      setRankingSection(visibleRankingSections[0]?.key || "");
    }
  }, [visibleRankingSections, rankingSection]);

  const riderResultValue = (entry) => {
    const raw = entry?.value || entry?.capital || entry?.bonus || entry?.gap || null;
    if (!raw) return null;
    return String(raw)
      .replace(/\bpts?\b/gi, "p")
      .replace(/\bsec\b/gi, "sn")
      .replace(/\s+/g, " ")
      .trim();
  };

  const closeRiderModal = () => {
    riderRequestRef.current += 1;
    setSelectedRider(null);
    setRiderDetails(null);
    setRiderLoading(false);
    setRiderError(null);
  };

  const openRiderModal = async (entry, context) => {
    if (!entry || typeof entry !== "object") return;
    const raceContextInput =
      context && typeof context === "object" ? context : { title: context || "Resmi Sonuç" };

    const nat = String(entry?.nationality || "")
      .trim()
      .toUpperCase();
    const image =
      absoluteMatsportUrl(entry?.image) ||
      absoluteMatsportUrl(entry?.photo) ||
      (entry?.uciid ? absoluteMatsportUrl(`/images/riders/${entry.uciid}/${entry.uciid}.png`) : null);

    const base = {
      uciid: entry?.uciid || null,
      name: entry?.name || (entry?.bib ? `Sporcu #${entry.bib}` : "Sporcu"),
      teamName: entry?.team || null,
      teamCode: entry?.teamCode || null,
      teamCategory: entry?.teamCategory || null,
      bib: entry?.bib || null,
      nationality: nat || null,
      countryNameTr: countryNameTr(nat) || null,
      image,
      birthDate: entry?.birthDate || null,
      status: entry?.status || null,
      contextTitle: raceContextInput?.title || "Resmi Sonuç",
      rank: entry?.position || raceContextInput?.rank || null,
      resultValue: riderResultValue(entry) || raceContextInput?.resultValue || null,
      raceContext: {
        title: raceContextInput?.title || "Resmi Sonuç",
        stageNumber: raceContextInput?.stageNumber || null,
        stageName: raceContextInput?.stageName || null,
        stageDate: raceContextInput?.stageDate || null,
        gateLabel: raceContextInput?.gateLabel || null,
        gateType: raceContextInput?.gateType || null,
        gateCategory: raceContextInput?.gateCategory || null,
        gateKm:
          raceContextInput?.gateKm === null || raceContextInput?.gateKm === undefined
            ? null
            : Number(raceContextInput.gateKm),
        sourceLabel: raceContextInput?.sourceLabel || null,
      },
      placeOfBirth: null,
      age: null,
      height: null,
      weight: null,
      socials: [],
      topResults: [],
    };

    setSelectedRider(entry);
    setRiderDetails(base);
    setRiderError(null);

    if (!entry?.uciid) return;

    const reqId = ++riderRequestRef.current;
    setRiderLoading(true);
    try {
      const res = await axios.get(API_RIDER, { params: { uciid: entry.uciid } });
      if (riderRequestRef.current !== reqId) return;

      const detail = res?.data?.data;
      if (!detail || typeof detail !== "object") {
        setRiderLoading(false);
        return;
      }

      const detailNat = String(detail.nationality || base.nationality || "")
        .trim()
        .toUpperCase();

      setRiderDetails((prev) => ({
        ...(prev || base),
        ...detail,
        name: detail.name || prev?.name || base.name,
        teamName: detail.teamName || prev?.teamName || base.teamName,
        teamCode: detail.teamCode || prev?.teamCode || base.teamCode,
        teamCategory: detail.teamCategory || prev?.teamCategory || base.teamCategory,
        nationality: detailNat || prev?.nationality || base.nationality,
        countryNameTr:
          countryNameTr(detailNat) ||
          detail.countryName ||
          prev?.countryNameTr ||
          base.countryNameTr,
        image:
          absoluteMatsportUrl(detail.image) ||
          absoluteMatsportUrl(detail.photo) ||
          prev?.image ||
          base.image,
        contextTitle: prev?.contextTitle || base.contextTitle,
        rank: prev?.rank || base.rank,
        resultValue: prev?.resultValue || base.resultValue,
        raceContext: prev?.raceContext || base.raceContext,
      }));
    } catch (err) {
      if (riderRequestRef.current !== reqId) return;
      setRiderError(err?.response?.data?.error || err.message || "Sporcu detayı alınamadı.");
    } finally {
      if (riderRequestRef.current === reqId) {
        setRiderLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedRider) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeRiderModal();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRider]);

  return (
    <section
      id="sonuclar"
      className="max-w-[1280px] mx-auto px-4 sm:px-6 mt-16 sm:mt-24"
      data-testid="results-section"
    >
      <StageSelector
        stages={stages}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
      />

      <div className="flex items-end justify-between flex-wrap gap-3 mb-6 mt-6">
        <div>
          <div className="kicker"><span className="bar" /> Sonuçlar</div>
          <h2
            className="font-display mt-2"
            style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", letterSpacing: "-0.015em", lineHeight: 1.05, color: "var(--ink)" }}
          >
            <span className="text-grad-turq">
              {stage?.stage ? `Etap ${stage.stage}` : "Seçili Etap"} Sonuç Merkezi
            </span>
          </h2>
          {selectedStageSummary.length ? (
            <div className="results-stage-chiprow">
              {selectedStageSummary.map((item) => (
                <span key={item} className="chip">{item}</span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="official-tabs results-mode-tabs" data-testid="results-mode-tabs">
          {Object.entries(RESULTS_MODE_META).map(([key, meta]) => (
            <button
              key={key}
              type="button"
              onClick={() => setResultsMode(key)}
              className={`official-tab-btn ${resultsMode === key ? "active" : ""}`}
            >
              {meta.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6 official-panel-card results-stage-card" data-testid="official-rankings-board">
        <div className="results-stage-section-head">
          <div>
            <div className="results-stage-section-title">{activeModeMeta?.label || "Sonuçlar"}</div>
          </div>
          {stageFeedNote ? (
            <div
              className="results-stage-note"
              style={{
                background: "rgba(10,169,193,0.08)",
                border: "1px solid rgba(10,169,193,0.18)",
                color: "var(--ink-2)",
              }}
              data-testid="results-stage-note"
            >
              {stageFeedNote}
            </div>
          ) : null}
        </div>

        {!visibleRankingSections.length ? (
          <div className="official-table-card results-stage-empty" style={{ color: "var(--ink-2)" }}>
            {resultsMode === "stage"
              ? "Seçili etap için resmi etap klasmanı henüz yayınlanmadı."
              : "Genel klasman panelleri açıklanınca burada otomatik oluşacak."}
          </div>
        ) : (
          <>
            <div className="official-tabs" data-testid="official-ranking-tabs">
              {visibleRankingSections.map((section) => (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setRankingSection(section.key)}
                  className={`official-tab-btn ${activeRankingSection?.key === section.key ? "active" : ""}`}
                >
                  {section.label}
                </button>
              ))}
            </div>

            <div className="official-ranking-grid mt-4" data-testid="official-ranking-grid">
              {(activeRankingSection?.panels || []).map((panel) => {
                const tables = panel.tables || [];
                const contextStage = resultsMode === "stage" ? stageResultsStage : generalResultsStage;
                return (
                  <article key={panel.key} className="official-panel-card">
                    <div className="official-panel-head">
                      <div className="official-panel-title">{panel.label}</div>
                    </div>

                    <div className="official-panel-stack">
                      {tables.map((table) => {
                        const showTableHead =
                          tables.length > 1 || Boolean(table.subtitle) || table.label !== panel.label;
                        return (
                          <div key={table.key} className="official-table-card">
                            {showTableHead ? (
                              <div className="official-table-head">
                                <div className="official-table-title">{table.label}</div>
                                {table.subtitle ? (
                                  <div className="official-table-sub">{table.subtitle}</div>
                                ) : null}
                              </div>
                            ) : null}

                            <div className="official-table-list">
                              {(table.rows || []).map((row, rowIdx) => {
                                if (table.kind === "team") {
                                  const jerseyUrl = absoluteMatsportUrl(row?.jersey2 || null);
                                  return (
                                    <div
                                      key={`${table.key}-team-${row?.position || rowIdx}`}
                                      className="official-team-row"
                                    >
                                      <span className="official-row-rank">{row?.position || rowIdx + 1}</span>
                                      <span className="official-team-main">
                                        <span className="official-team-name-wrap">
                                          {jerseyUrl ? (
                                            <img
                                              src={jerseyUrl}
                                              alt={row?.teamCode || row?.teamName || "Takım"}
                                              className="official-team-jersey"
                                              loading="lazy"
                                            />
                                          ) : null}
                                          <span className="official-row-name">
                                            {row?.teamName || `Takım ${row?.teamPosition || rowIdx + 1}`}
                                          </span>
                                        </span>
                                        <span className="official-row-meta">
                                          {[row?.teamCode, teamCategoryLabel(row?.teamCategory || ""), row?.nationality]
                                            .filter(Boolean)
                                            .join(" · ") || "Takım bilgisi"}
                                        </span>
                                      </span>
                                      <span className="official-row-value">
                                        {riderResultValue(row) || "—"}
                                      </span>
                                    </div>
                                  );
                                }

                                const natCode = String(row?.nationality || "").trim().toUpperCase();
                                const flagUrl = natCode ? `${MATS_API_BASE}/images/flags/${natCode}.png` : null;
                                return (
                                  <button
                                    key={`${table.key}-rider-${row?.position || rowIdx}`}
                                    type="button"
                                    className="official-ranking-row"
                                    onClick={() =>
                                      openRiderModal(row, {
                                        title: panel.label || activeRankingSection?.label || activeModeMeta?.label,
                                        stageNumber: contextStage?.stage || null,
                                        stageName: contextStage?.name || null,
                                        stageDate: contextStage?.date || null,
                                        gateLabel: table?.label || panel?.label || null,
                                        gateType: activeRankingSection?.label || null,
                                        sourceLabel: table?.subtitle || activeModeMeta?.label || null,
                                        rank: row?.position || rowIdx + 1,
                                        resultValue: riderResultValue(row),
                                      })
                                    }
                                  >
                                    <span className="official-row-rank">{row?.position || rowIdx + 1}</span>
                                    <span className="official-row-main">
                                      <span className="official-row-name-wrap">
                                        {flagUrl ? (
                                          <img
                                            src={flagUrl}
                                            alt={natCode}
                                            className="rider-flag"
                                            loading="lazy"
                                            onError={(e) => {
                                              e.currentTarget.style.display = "none";
                                            }}
                                          />
                                        ) : null}
                                        <span className="official-row-name">
                                          {row?.name || (row?.bib ? `Sporcu #${row.bib}` : "Sporcu")}
                                        </span>
                                      </span>
                                      <span className="official-row-meta">
                                        {row?.team || row?.teamName || row?.teamCode || "Takım bilgisi"}
                                      </span>
                                    </span>
                                    <span className="official-row-value">
                                      {riderResultValue(row) || "—"}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>

      {resultsMode === "general" ? (
        <div className="official-panel-card results-stage-card" data-testid="leader-jerseys-grid">
          <div className="results-stage-section-head">
            <div>
              <div className="results-stage-section-title">Lider Mayolar</div>
            </div>
            <div
              className="results-stage-note"
              style={{
                background: "rgba(10,169,193,0.08)",
                border: "1px solid rgba(10,169,193,0.18)",
                color: "var(--ink-2)",
              }}
            >
              {jerseyNote}
            </div>
          </div>
          {leaders.length === 0 ? (
            <div className="official-table-card results-stage-empty" style={{ color: "var(--ink-2)" }}>
              Mayo liderliği verisi geldiğinde burada otomatik görünecek.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {leaders.map((leader) => {
                const theme = jerseyTheme(leader.theme);
                const jerseySrc = JERSEY_IMAGES[theme];
                const podium = Array.isArray(leader?.podium) ? leader.podium.slice(0, 3) : [];
                return (
                  <div key={leader.key} className={`jersey-card ${theme}`} data-testid={`jersey-${leader.key}`}>
                    <div className="jersey-bar" />
                    <div className="flex items-center gap-3.5">
                      <div className="jersey-icon" aria-hidden="true">
                        {jerseySrc ? (
                          <img src={jerseySrc} alt={`${leader.jerseyName || "Lider Mayo"} forma`} loading="lazy" />
                        ) : (
                          <Bike size={24} strokeWidth={2.4} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div
                          className="font-mono text-[0.66rem] tracking-[0.2em] uppercase font-bold"
                          style={{ color: "var(--turquoise-deep)" }}
                        >
                          {leader.jerseyName || "Lider Mayo"}
                        </div>
                        <h4
                          className="font-display mt-1 text-[1.18rem] leading-tight"
                          style={{ letterSpacing: "-0.01em", color: "var(--ink)" }}
                        >
                          {leader.title || "Klasman"}
                        </h4>
                      </div>
                    </div>

                    <div
                      className="mt-5 pt-4 border-t font-mono text-[0.7rem] uppercase tracking-[0.18em]"
                      style={{ color: "var(--text-muted)", borderColor: "rgba(10,169,193,0.14)" }}
                    >
                      Lider Sporcu
                    </div>
                    <div
                      className="mt-1.5 font-display text-[1.18rem] leading-tight"
                      style={{ color: "var(--ink)", letterSpacing: "-0.005em" }}
                    >
                      {leader.leaderName || "Henüz açıklanmadı"}
                    </div>
                    <div className="mt-1 text-[0.88rem]" style={{ color: "var(--ink-2)" }}>
                      {[leader.leaderTeam, leader.leaderTeamCode, leader.leaderNationality]
                        .filter(Boolean)
                        .join(" · ") || "Takım bilgisi bekleniyor"}
                    </div>

                    <div className="mt-4 flex items-center gap-2">
                      <span
                        className="font-mono text-[0.78rem] px-3 py-1.5 rounded-full font-bold"
                        style={{
                          border: "1px solid rgba(10,169,193,0.22)",
                          background: "rgba(10,169,193,0.1)",
                          color: "var(--turquoise-deep)",
                        }}
                      >
                        {leader.leaderBib ? `#${leader.leaderBib}` : "#—"}
                      </span>
                      <span
                        className="font-mono text-[0.78rem] px-3 py-1.5 rounded-full ml-auto font-semibold"
                        style={{
                          border: "1px solid rgba(10,169,193,0.18)",
                          background: "#fff",
                          color: "var(--ink)",
                        }}
                      >
                        {leader.classificationValue || "Veri bekleniyor"}
                      </span>
                    </div>

                    {podium.length ? (
                      <div className="leader-podium mt-3.5">
                        <div className="leader-podium-head">1 · 2 · 3</div>
                        <div className="leader-podium-list">
                          {podium.map((row, rowIdx) => {
                            const natCode = String(row?.nationality || "").trim().toUpperCase();
                            const flagUrl = natCode ? `${MATS_API_BASE}/images/flags/${natCode}.png` : null;
                            const riderMeta = row?.team || row?.teamName || row?.teamCode || null;
                            return (
                              <button
                                key={`${leader.key}-podium-${row?.position || rowIdx}`}
                                type="button"
                                className="leader-podium-row"
                                onClick={() =>
                                  openRiderModal(row, {
                                    title: `${leader.title || "Klasman"} · İlk 3`,
                                    stageNumber: generalResultsStage?.stage || latestSource?.stage || null,
                                    stageName: generalResultsStage?.name || latestSource?.name || null,
                                    stageDate: generalResultsStage?.date || latestSource?.date || null,
                                    gateLabel: leader?.title || "Lider Mayo Klasmanı",
                                    gateType: leader?.jerseyName || null,
                                    sourceLabel: "Lider Mayo",
                                    rank: row?.position || rowIdx + 1,
                                    resultValue: riderResultValue(row),
                                  })
                                }
                              >
                                <span className="leader-podium-pos">{row?.position || rowIdx + 1}</span>
                                <span className="leader-podium-name">
                                  {flagUrl ? (
                                    <img
                                      src={flagUrl}
                                      alt={natCode}
                                      className="rider-flag"
                                      loading="lazy"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                      }}
                                    />
                                  ) : null}
                                  <span className="truncate">
                                    {row?.name || (row?.bib ? `Sporcu #${row.bib}` : "Sporcu")}
                                  </span>
                                </span>
                                <span className="leader-podium-meta">
                                  {riderMeta || "—"}
                                </span>
                                <span className="leader-podium-value">{riderResultValue(row) || "—"}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    {leader.isLoaned && leader.jerseyOwnerBib ? (
                      <div className="mt-3 text-[0.74rem]" style={{ color: "var(--text-muted)" }}>
                        Asıl lider #{leader.jerseyOwnerBib}, mayoyu farklı sporcu taşıyabilir.
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {selectedRider ? (
        <div
          className="fixed inset-0 z-50 p-4 sm:p-6 grid place-items-center"
          style={{ background: "rgba(3,4,8,0.72)", backdropFilter: "blur(10px)" }}
          onClick={closeRiderModal}
          data-testid="live-rider-modal"
        >
          <div
            className="rider-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeRiderModal}
              className="rider-modal-close"
              aria-label="Kapat"
            >
              <X size={16} />
            </button>

            <div className="rider-modal-head">
              {riderDetails?.image ? (
                <img
                  src={riderDetails.image}
                  alt={riderDetails.name || "Sporcu"}
                  className="rider-modal-avatar"
                />
              ) : (
                <span className="rider-modal-avatar rider-modal-avatar-fallback">
                  {(riderDetails?.name || "S").charAt(0).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <div className="font-display text-[1.25rem] sm:text-[1.5rem] leading-tight truncate" style={{ color: "var(--ink)" }}>
                  {riderDetails?.name || "Sporcu"}
                </div>
                <div className="mt-1 text-[0.82rem] font-mono tracking-[0.08em] uppercase truncate" style={{ color: "var(--ink-2)" }}>
                  {riderDetails?.teamName || riderDetails?.teamCode || "Takım bilgisi yok"}
                </div>
                <div className="mt-2 flex items-center flex-wrap gap-2">
                  <span className="chip">#{riderDetails?.bib || "—"}</span>
                  {riderDetails?.nationality ? (
                    <span className="chip">
                      <img
                        src={`${MATS_API_BASE}/images/flags/${String(riderDetails.nationality).toUpperCase()}.png`}
                        alt={riderDetails.nationality}
                        className="rider-flag"
                      />
                      {riderDetails.nationality}
                    </span>
                  ) : null}
                  <span className="chip">{riderDetails?.countryNameTr || "Ülke bilgisi yok"}</span>
                </div>
              </div>
            </div>

            <div className="rider-modal-grid">
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Etap</div>
                <div className="mt-1.5 font-semibold leading-tight" style={{ color: "var(--ink)" }}>
                  {riderDetails?.raceContext?.stageNumber
                    ? `Etap ${riderDetails.raceContext.stageNumber} · ${shortRoute(
                        riderDetails?.raceContext?.stageName || ""
                      )}`
                    : "Etap bilgisi bekleniyor"}
                </div>
                <div className="text-[0.78rem] mt-1 font-mono" style={{ color: "var(--ink-2)" }}>
                  {riderDetails?.raceContext?.stageDate
                    ? dateText(riderDetails.raceContext.stageDate)
                    : "Tarih bekleniyor"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Kapı / Klasman</div>
                <div className="mt-1.5 font-semibold leading-tight" style={{ color: "var(--ink)" }}>
                  {riderDetails?.raceContext?.gateLabel || riderDetails?.contextTitle || "Resmi Sonuç"}
                </div>
                <div className="text-[0.78rem] mt-1 font-mono" style={{ color: "var(--ink-2)" }}>
                  {[
                    riderDetails?.raceContext?.gateType || null,
                    riderDetails?.raceContext?.gateKm !== null &&
                    riderDetails?.raceContext?.gateKm !== undefined
                      ? kmText(riderDetails.raceContext.gateKm, 1)
                      : null,
                    riderDetails?.raceContext?.gateCategory
                      ? `Kategori ${riderDetails.raceContext.gateCategory}`
                      : null,
                    riderDetails?.raceContext?.sourceLabel || null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "Detay bekleniyor"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Derece</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.rank ? `${riderDetails.rank}. sıra` : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Sonuç</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.resultValue || "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Doğum Tarihi</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.birthDate ? dateText(riderDetails.birthDate) : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Yaş</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.age ?? "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Boy</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.height ? `${riderDetails.height} cm` : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Kilo</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.weight ? `${riderDetails.weight} kg` : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Doğum Yeri</div>
                <div className="mt-1.5 font-semibold truncate" style={{ color: "var(--ink)" }}>
                  {riderDetails?.placeOfBirth || "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Durum</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderStatusTr(riderDetails?.status) || "Aktif"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Takım Kategori</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {teamCategoryLabel(riderDetails?.teamCategory || "") || "—"}
                </div>
              </div>
            </div>

            {(riderDetails?.socials || []).length > 0 ? (
              <div className="mt-4">
                <div className="kicker mb-2"><span className="bar" /> Sosyal Medya</div>
                <div className="flex flex-wrap gap-2">
                  {riderDetails.socials.map((s, i) => (
                    <a
                      key={`${s.platform || "social"}-${i}`}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="chip"
                    >
                      {s.platform || "Profil"}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}

            {(riderDetails?.topResults || []).length > 0 ? (
              <div className="mt-4">
                <div className="kicker mb-2"><span className="bar" /> Öne Çıkan Sonuçlar</div>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {riderDetails.topResults.slice(0, 4).map((r, i) => (
                    <div key={`${r.raceName || "race"}-${i}`} className="event-tile">
                      <div className="font-semibold text-[0.93rem]" style={{ color: "var(--ink)" }}>
                        {r.raceName || "Yarış"}
                      </div>
                      <div className="text-[0.8rem] mt-1 font-mono" style={{ color: "var(--ink-2)" }}>
                        {r.date || "—"} · {riderResultTypeTr(r.type)} · {r.pos ? `${r.pos}.` : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {riderLoading ? (
              <div className="mt-4 text-[0.85rem] font-semibold" style={{ color: "var(--turquoise-deep)" }}>
                Sporcu detayı yükleniyor…
              </div>
            ) : null}
            {riderError ? (
              <div className="mt-4 text-[0.85rem] font-semibold" style={{ color: "#b91c1c" }}>
                {riderError}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}


function TeamsPanel({ teams }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("ALL");
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedRider, setSelectedRider] = useState(null);
  const [riderDetails, setRiderDetails] = useState(null);
  const [riderLoading, setRiderLoading] = useState(false);
  const [riderError, setRiderError] = useState(null);
  const riderRequestRef = useRef(0);

  const categories = useMemo(
    () => Array.from(new Set((teams || []).map((t) => t.category || "—"))).sort(),
    [teams]
  );

  const filtered = useMemo(() => {
    const q = normalize(search);
    return (teams || []).filter((team) => {
      if (category !== "ALL" && (team.category || "—") !== category) return false;
      if (!q) return true;
      const hay = [
        team.name,
        team.code,
        team.category,
        team.nationality,
        ...(team.riders || []).map((r) => `${r.name} ${r.nationality || ""}`),
      ].join(" ");
      return normalize(hay).includes(q);
    });
  }, [teams, search, category]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const closeRiderModal = () => {
    riderRequestRef.current += 1;
    setSelectedRider(null);
    setRiderDetails(null);
    setRiderLoading(false);
    setRiderError(null);
  };

  const openRiderModal = async (team, rider) => {
    const nat = String(rider?.nationality || team?.nationality || "")
      .trim()
      .toUpperCase();
    const image =
      absoluteMatsportUrl(rider?.image) ||
      absoluteMatsportUrl(rider?.photo) ||
      (rider?.uciid ? absoluteMatsportUrl(`/images/riders/${rider.uciid}/${rider.uciid}.png`) : null);

    const base = {
      uciid: rider?.uciid || null,
      name: rider?.name || "Sporcu",
      teamName: team?.name || null,
      teamCategory: team?.category || null,
      bib: rider?.engaged || null,
      nationality: nat || null,
      countryNameTr: countryNameTr(nat) || null,
      image,
      birthDate: rider?.birthDate || null,
      status: rider?.status || null,
      placeOfBirth: null,
      age: null,
      height: null,
      weight: null,
      careerProWins: null,
      grandTours: null,
      monuments: null,
      socials: [],
      topResults: [],
    };

    setSelectedRider({ rider, team });
    setRiderDetails(base);
    setRiderError(null);

    if (!rider?.uciid) {
      return;
    }

    const reqId = ++riderRequestRef.current;
    setRiderLoading(true);
    try {
      const res = await axios.get(API_RIDER, { params: { uciid: rider.uciid } });
      if (riderRequestRef.current !== reqId) return;

      const detail = res?.data?.data;
      if (!detail || typeof detail !== "object") {
        setRiderLoading(false);
        return;
      }

      const detailNat = String(detail.nationality || base.nationality || "")
        .trim()
        .toUpperCase();

      setRiderDetails((prev) => ({
        ...(prev || base),
        ...detail,
        name: detail.name || prev?.name || base.name,
        teamName: detail.teamName || prev?.teamName || base.teamName,
        nationality: detailNat || prev?.nationality || base.nationality,
        countryNameTr:
          countryNameTr(detailNat) ||
          detail.countryName ||
          prev?.countryNameTr ||
          base.countryNameTr,
        image:
          absoluteMatsportUrl(detail.image) ||
          absoluteMatsportUrl(detail.photo) ||
          prev?.image ||
          base.image,
      }));
    } catch (err) {
      if (riderRequestRef.current !== reqId) return;
      setRiderError(err?.response?.data?.error || err.message || "Sporcu detayı alınamadı.");
    } finally {
      if (riderRequestRef.current === reqId) {
        setRiderLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedRider) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeRiderModal();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRider]);

  return (
    <>
      <section
        id="takimlar"
        className="max-w-[1280px] mx-auto px-4 sm:px-6 mt-16 sm:mt-24"
        data-testid="team-squads-table"
      >
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="kicker"><span className="bar" /> Start Listesi</div>
            <h2
              className="font-display mt-2"
              style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", letterSpacing: "-0.015em", lineHeight: 1.05, color: "var(--ink)" }}
            >
              Pelotonun <span className="text-grad-turq">{teams?.length || 0} Takımı</span>
            </h2>
          </div>
          <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(220px,1fr)_180px] gap-2 w-full sm:w-auto sm:min-w-[460px]">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-muted)" }} />
              <input
                type="text"
                placeholder="Takım veya sporcu ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="tur-input !pl-9"
                data-testid="team-search-input"
              />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="tur-select"
              data-testid="team-category-select"
            >
              <option value="ALL">Tüm Kategoriler</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {teamCategoryLabel(c)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="glass p-6 text-[0.95rem]" style={{ color: "var(--ink-2)" }}>
            Aramanıza uyan takım bulunamadı.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((team) => {
              const key = team.code || team.name;
              const isExpanded = expanded.has(key);
              const visibleRiders = isExpanded ? team.riders : (team.riders || []).slice(0, 8);
              return (
                <div key={key} className="team-card" data-testid={`team-card-${team.code || team.name}`}>
                  <div className="p-4 flex items-center gap-3.5">
                    {team.jersey2 ? (
                      <img
                        src={`${MATS_API_BASE}${team.jersey2}`}
                        alt={`${team.name} forma`}
                        className="team-jersey"
                        loading="lazy"
                      />
                    ) : (
                      <div className="team-jersey grid place-items-center" style={{ color: "var(--turquoise-deep)" }}>
                        <Bike size={18} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-[1.08rem] leading-tight truncate" style={{ color: "var(--ink)", fontWeight: 600 }}>
                        {team.name || "Takım"}
                      </div>
                      <div className="text-[0.8rem] mt-0.5 font-mono uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--ink-2)" }}>
                        {teamCategoryLabel(team.category)} · {team.nationality || "—"}
                      </div>
                    </div>
                    <div
                      className="font-mono text-[0.78rem] px-3 py-1.5 rounded-full whitespace-nowrap font-bold"
                      style={{
                        border: "1px solid rgba(10,169,193,0.22)",
                        background: "rgba(10,169,193,0.1)",
                        color: "var(--turquoise-deep)",
                      }}
                    >
                      {team.ridersCount || 0} sporcu
                    </div>
                  </div>
                  <ul style={{ borderTop: "1px solid rgba(10,169,193,0.1)" }}>
                    {(visibleRiders || []).map((rider, i) => {
                      const natCode = String(rider.nationality || team.nationality || "").trim().toUpperCase();
                      const flagUrl = natCode ? `${MATS_API_BASE}/images/flags/${natCode}.png` : null;
                      const riderImage =
                        absoluteMatsportUrl(rider.image) ||
                        absoluteMatsportUrl(rider.photo) ||
                        (rider.uciid ? absoluteMatsportUrl(`/images/riders/${rider.uciid}/${rider.uciid}.png`) : null);

                      return (
                        <li key={`${key}-${rider.engaged || rider.uciid || i}`} className="rider-row" style={{ color: "var(--ink)" }}>
                          <button
                            type="button"
                            className="rider-row-btn"
                            onClick={() => openRiderModal(team, rider)}
                            data-testid={`rider-row-${team.code || "team"}-${rider.engaged || i}`}
                          >
                            <span className="bib">#{rider.engaged || "—"}</span>
                            <span className="rider-main">
                              {riderImage ? (
                                <img
                                  src={riderImage}
                                  alt={rider.name || "Sporcu"}
                                  className="rider-avatar"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : (
                                <span className="rider-avatar-fallback">
                                  {(rider.name || "S").charAt(0).toUpperCase()}
                                </span>
                              )}
                              <span className="truncate">{rider.name || "Sporcu"}</span>
                            </span>
                            <span className="nat">
                              {flagUrl ? (
                                <img
                                  src={flagUrl}
                                  alt={natCode}
                                  className="rider-flag"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : null}
                              {natCode || "—"}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {(team.riders || []).length > 8 ? (
                    <button
                      type="button"
                      onClick={() => toggle(key)}
                      className="w-full px-4 py-3 text-[0.84rem] font-semibold transition-colors"
                      style={{
                        color: "var(--turquoise-deep)",
                        borderTop: "1px solid rgba(10,169,193,0.1)",
                        background: "rgba(10,169,193,0.04)",
                      }}
                      data-testid={`team-toggle-${team.code || team.name}`}
                    >
                      {isExpanded
                        ? "Daha az göster"
                        : `Tüm kadroyu göster (${team.riders.length})`}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {selectedRider ? (
        <div
          className="fixed inset-0 z-50 p-4 sm:p-6 grid place-items-center"
          style={{ background: "rgba(3,4,8,0.72)", backdropFilter: "blur(10px)" }}
          onClick={closeRiderModal}
          data-testid="rider-modal"
        >
          <div
            className="rider-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeRiderModal}
              className="rider-modal-close"
              aria-label="Kapat"
            >
              <X size={16} />
            </button>

            <div className="rider-modal-head">
              {riderDetails?.image ? (
                <img
                  src={riderDetails.image}
                  alt={riderDetails.name || "Sporcu"}
                  className="rider-modal-avatar"
                />
              ) : (
                <span className="rider-modal-avatar rider-modal-avatar-fallback">
                  {(riderDetails?.name || "S").charAt(0).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <div className="font-display text-[1.25rem] sm:text-[1.5rem] leading-tight truncate" style={{ color: "var(--ink)" }}>
                  {riderDetails?.name || "Sporcu"}
                </div>
                <div className="mt-1 text-[0.82rem] font-mono tracking-[0.08em] uppercase truncate" style={{ color: "var(--ink-2)" }}>
                  {riderDetails?.teamName || "Takım bilgisi yok"}
                </div>
                <div className="mt-2 flex items-center flex-wrap gap-2">
                  <span className="chip">#{riderDetails?.bib || "—"}</span>
                  {riderDetails?.nationality ? (
                    <span className="chip">
                      <img
                        src={`${MATS_API_BASE}/images/flags/${String(riderDetails.nationality).toUpperCase()}.png`}
                        alt={riderDetails.nationality}
                        className="rider-flag"
                      />
                      {riderDetails.nationality}
                    </span>
                  ) : null}
                  <span className="chip">{riderDetails?.countryNameTr || "Ülke bilgisi yok"}</span>
                </div>
              </div>
            </div>

            <div className="rider-modal-grid">
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Doğum Tarihi</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.birthDate ? dateText(riderDetails.birthDate) : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Yaş</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.age ?? "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Boy</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.height ? `${riderDetails.height} cm` : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Kilo</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderDetails?.weight ? `${riderDetails.weight} kg` : "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Doğum Yeri</div>
                <div className="mt-1.5 font-semibold truncate" style={{ color: "var(--ink)" }}>
                  {riderDetails?.placeOfBirth || "—"}
                </div>
              </div>
              <div className="event-tile">
                <div className="kicker"><span className="bar" /> Durum</div>
                <div className="mt-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                  {riderStatusTr(riderDetails?.status) || "Aktif"}
                </div>
              </div>
            </div>

            {(riderDetails?.socials || []).length > 0 ? (
              <div className="mt-4">
                <div className="kicker mb-2"><span className="bar" /> Sosyal Medya</div>
                <div className="flex flex-wrap gap-2">
                  {riderDetails.socials.map((s, i) => (
                    <a
                      key={`${s.platform || "social"}-${i}`}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="chip"
                    >
                      {s.platform || "Profil"}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}

            {(riderDetails?.topResults || []).length > 0 ? (
              <div className="mt-4">
                <div className="kicker mb-2"><span className="bar" /> Öne Çıkan Sonuçlar</div>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {riderDetails.topResults.slice(0, 4).map((r, i) => (
                    <div key={`${r.raceName || "race"}-${i}`} className="event-tile">
                      <div className="font-semibold text-[0.93rem]" style={{ color: "var(--ink)" }}>
                        {r.raceName || "Yarış"}
                      </div>
                      <div className="text-[0.8rem] mt-1 font-mono" style={{ color: "var(--ink-2)" }}>
                        {r.date || "—"} · {riderResultTypeTr(r.type)} · {r.pos ? `${r.pos}.` : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {riderLoading ? (
              <div className="mt-4 text-[0.85rem] font-semibold" style={{ color: "var(--turquoise-deep)" }}>
                Sporcu detayı yükleniyor…
              </div>
            ) : null}
            {riderError ? (
              <div className="mt-4 text-[0.85rem] font-semibold" style={{ color: "#b91c1c" }}>
                {riderError}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

function Footer({ lastUpdated }) {
  return (
    <footer className="footer-grad mt-12 px-4 sm:px-6 py-12" data-testid="site-footer">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex items-start justify-between gap-8 flex-wrap">
          <div className="max-w-2xl">
            <div
              className="font-display text-[1.6rem] sm:text-[1.95rem] leading-tight"
              style={{ letterSpacing: "-0.01em", color: "var(--ink)" }}
            >
              <span className="text-grad-turq">61. Cumhurbaşkanlığı</span>
              <br />
              Bisiklet Turu · Tour of Türkiye 2026
            </div>
            <p className="mt-4 text-[0.92rem] leading-relaxed" style={{ color: "var(--ink-2)" }}>
              BikePedia Yarış Merkezi · Yarışa dair tüm sporcu, takım, etap,
              klasman ve zaman verileri resmi yarış feed’inden anlık olarak
              alınır, sürekli güncellenir ve tek ekranda sunulur. Etap
              gelişmeleri, genel klasman değişimleri ve yarış içi kritik
              verileri canlı olarak takip edebilirsiniz.
            </p>
          </div>
          <a
            href="https://bikepedia.com.tr"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center"
            data-testid="footer-brand"
          >
            <img src={LOGO_BP} alt="BikePedia" className="w-14 h-14 rounded-full" />
          </a>
        </div>
        <div
          className="mt-10 pt-6 border-t flex flex-wrap items-center justify-between gap-3 text-[0.78rem] font-mono"
          style={{ borderColor: "rgba(10,169,193,0.14)", color: "var(--text-muted)" }}
        >
          <span>© 2026 Tour of Türkiye Race Center · BikePedia</span>
          <span>Son güncelleme: {dateTimeText(lastUpdated)}</span>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  const [payload, setPayload] = useState(null);
  const [meta, setMeta] = useState({ source: null, fetchedAt: null, error: null, refreshPolicy: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentView, setCurrentView] = useState(() =>
    typeof window !== "undefined" ? viewFromHash(window.location.hash) : DEFAULT_VIEW
  );
  const [error, setError] = useState(null);
  const stagesRef = useRef([]);

  const loadData = async (force = false) => {
    if (force) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(API_COMPETITION, { params: force ? { force: 1 } : {} });
      const data = res.data?.data;
      if (!data) throw new Error("Yarış verisi alınamadı.");
      setPayload(data);
      setMeta({
        source: res.data?.source,
        fetchedAt: res.data?.fetchedAt,
        error: res.data?.error,
        refreshPolicy: res.data?.refreshPolicy,
      });
      setError(null);
      // Pick today's stage by default on first load only
      if (!force) {
        setActiveIndex(pickInitialStageIndex(data.stages || []));
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Hata");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData(false);
    // periodic auto-refresh every 5 minutes only during race + 1 hour
    const id = setInterval(() => {
      if (shouldAutoRefreshNow(stagesRef.current)) {
        loadData(false);
      }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncView = () => {
      const nextView = viewFromHash(window.location.hash);
      setCurrentView(nextView);
    };
    if (!window.location.hash) {
      window.history.replaceState(null, "", `#${DEFAULT_VIEW}`);
    }
    syncView();
    window.addEventListener("hashchange", syncView);
    return () => window.removeEventListener("hashchange", syncView);
  }, []);

  const stages = useMemo(() => payload?.stages || [], [payload?.stages]);
  const teams = useMemo(() => payload?.teams || [], [payload?.teams]);
  const competition = payload?.competition;
  const summary = payload?.summary;
  const jerseyLeaders = payload?.jerseyLeaders;
  const activeStage = stages[activeIndex];

  useEffect(() => {
    stagesRef.current = stages;
  }, [stages]);

  const status = useRaceStatus(stages);

  return (
    <div className="min-h-screen relative">
      <NavBar
        now={new Date().toISOString()}
        onRefresh={() => loadData(true)}
        refreshing={refreshing}
        lastUpdated={meta.fetchedAt}
      />

      <HeroSection
        competition={competition}
        summary={summary}
        status={status}
      />

      <ExperienceSwitch currentView={currentView} />

      {error ? (
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 mb-6">
          <div
            className="glass p-4 text-[0.92rem]"
            style={{ color: "#b91c1c", border: "1px solid rgba(239,68,68,0.3)" }}
            data-testid="error-banner"
          >
            Veri çekiminde hata: {error}
          </div>
        </div>
      ) : null}

      {currentView === "etaplar" ? (
        <section
          id="etaplar"
          className="max-w-[1280px] mx-auto px-4 sm:px-6 mt-2"
          data-testid="stage-center-section"
        >
          {loading && !payload ? (
            <div className="glass p-12 text-center" style={{ color: "var(--ink-2)" }}>Yarış verileri yükleniyor…</div>
          ) : stages.length ? (
            <>
              <StageSelector
                stages={stages}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
              />
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStage?.id || activeIndex}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.35 }}
                >
                  <StageDetail
                    stage={activeStage}
                    activeIndex={activeIndex}
                  />
                  <StageResultsSection stages={stages} activeIndex={activeIndex} />
                </motion.div>
              </AnimatePresence>
            </>
          ) : (
            <div className="glass p-12 text-center" style={{ color: "var(--ink-2)" }}>Etap verisi bulunamadı.</div>
          )}
        </section>
      ) : null}

      {currentView === "sonuclar" ? (
        <ClassificationSection
          stages={stages}
          activeIndex={activeIndex}
          jerseyLeaders={jerseyLeaders}
          setActiveIndex={setActiveIndex}
        />
      ) : null}

      {currentView === "takimlar" ? <TeamsPanel teams={teams} /> : null}

      <Footer lastUpdated={meta.fetchedAt} />
    </div>
  );
}
